import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileSpreadsheet, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { inr, SUBCATS } from "@/lib/calc";
import { catLabel, localDay, exportRangeXlsx } from "@/lib/export";
import { useFeeMaster } from "@/lib/hooks";
import { Leaderboard } from "@/components/Leaderboard";
import { SpotExceptions } from "@/components/SpotExceptions";
import { LateEntries } from "@/components/LateEntries";
import { useAuth } from "@/lib/auth";
import { ReferralBoard } from "@/components/ReferralBoard";

const Kpi = ({ label, value, testid, money }) => (
  <div className="rounded-lg border border-plum-900/10 bg-white px-5 py-4">
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="font-num mt-1.5 text-2xl font-semibold text-plum-950" data-testid={testid}>{money ? `₹ ${inr(value)}` : value}</div>
  </div>
);

const Bars = ({ title, items, testid }) => {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div className="rounded-lg border border-plum-900/10 bg-white p-5" data-testid={testid}>
      <h2 className="font-display mb-4 text-base font-bold text-plum-950">{title}</h2>
      <div className="space-y-2">
        {items.map((i) => (
          <div key={i.label} className="grid grid-cols-[110px_1fr_32px] items-center gap-3 text-sm">
            <span className="truncate text-slate-600">{i.label}</span>
            <div className="h-2.5 rounded-full bg-slate-100">
              <div className="h-2.5 rounded-full bg-plum-600 transition-[width] duration-500" style={{ width: `${(i.count / max) * 100}%` }} />
            </div>
            <span className="font-num text-right font-medium" data-testid={`${testid}-${i.label.replace(/\s+/g, "-").toLowerCase()}`}>{i.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const shift = (d, n) => { const x = new Date(d + "T00:00:00"); x.setDate(x.getDate() + n); return x.toLocaleDateString("en-CA"); };

const fmtLong = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
const ymd = (x) => x.toLocaleDateString("en-CA");
const PRESETS = [
  { id: "today", label: "Today", range: () => { const t = ymd(new Date()); return [t, t]; } },
  { id: "yesterday", label: "Yesterday", range: () => { const t = shift(ymd(new Date()), -1); return [t, t]; } },
  { id: "7d", label: "Last 7 days", range: () => { const t = ymd(new Date()); return [shift(t, -6), t]; } },
  { id: "month", label: "This month", range: () => { const n = new Date(); return [ymd(new Date(n.getFullYear(), n.getMonth(), 1)), ymd(n)]; } },
  { id: "last-month", label: "Last month", range: () => { const n = new Date(); return [ymd(new Date(n.getFullYear(), n.getMonth() - 1, 1)), ymd(new Date(n.getFullYear(), n.getMonth(), 0))]; } },
];

export default function Dashboard() {
  const nav = useNavigate();
  const { isAdmin } = useAuth();
  const today = new Date().toLocaleDateString("en-CA");
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const { data: fm } = useFeeMaster();
  const { data = [] } = useQuery({ queryKey: ["receipts"], queryFn: () => api.get("/receipts").then((r) => r.data) });
  const rows = useMemo(() => data.filter((r) => { const d = localDay(r.created_at); return d >= from && d <= to; }), [data, from, to]);
  const span = Math.round((new Date(to) - new Date(from)) / 86400000) + 1;
  const move = (dir) => { setFrom(shift(from, dir * span)); setTo(shift(to, dir * span)); };
  const setRange = ([a, b]) => { setFrom(a); setTo(b); };
  const fileTag = from === to ? from : `${from}_to_${to}`;
  const sum = (f) => rows.reduce((s, r) => s + f(r.computed), 0);
  const formFees = data.filter((r) => r.form_fee && r.form_fee.date >= from && r.form_fee.date <= to);

  const byGrade = (fm?.order || []).map((g) => ({ label: g, count: rows.filter((r) => r.inputs.grade === g).length }));
  const byCat = [
    { label: "Spot", count: rows.filter((r) => r.inputs.spot).length },
    { label: "Revisit", count: rows.filter((r) => r.inputs.revisit).length },
  ];
  const bySub = SUBCATS.map((s) => ({ label: s.label, count: rows.filter((r) => r.inputs.sub?.[s.key]).length }));

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div className="fade-up">
          <div className="text-xs uppercase tracking-[0.2em] text-plum-700" data-testid="dash-range-label">
            {from === to ? fmtLong(from) : `${fmtLong(from)} — ${fmtLong(to)} · ${span} days`}
          </div>
          <h1 className="font-display mt-0.5 text-3xl font-bold leading-tight tracking-tight text-plum-950 sm:text-4xl">Summary</h1>
        </div>
        <Button disabled={!rows.length} onClick={() => exportRangeXlsx(rows, fm?.order || [], `Receipts_${fileTag}.xlsx`)} className="bg-plum-700 hover:bg-plum-800" data-testid="dash-export-btn">
          <FileSpreadsheet className="mr-2 h-4 w-4" />Export to Excel
        </Button>
      </div>

      <div className="fade-up mb-6 flex flex-wrap items-center gap-2 rounded-lg border border-plum-900/10 bg-white p-3">
        <div className="flex flex-wrap gap-1">
          {PRESETS.map((p) => (
            <Button key={p.id} size="sm" variant="ghost" onClick={() => setRange(p.range())} data-testid={`dash-preset-${p.id}`}
              className={`${p.range().join() === [from, to].join() ? "bg-plum-900 text-white hover:bg-plum-800 hover:text-white" : "text-slate-600"}`}>{p.label}</Button>
          ))}
        </div>
        <span className="mx-1 hidden h-6 w-px bg-slate-200 md:block" />
        <div className="flex flex-wrap items-center gap-2">
          <Button size="icon" variant="outline" onClick={() => move(-1)} data-testid="dash-prev-range"><ChevronLeft className="h-4 w-4" /></Button>
          <Input type="date" value={from} max={to} onChange={(e) => { const v = e.target.value; if (!v) return; setFrom(v); if (v > to) setTo(v); }} className="w-40" data-testid="dash-from-input" />
          <span className="text-xs text-slate-500">to</span>
          <Input type="date" value={to} min={from} onChange={(e) => { const v = e.target.value; if (!v) return; setTo(v); if (v < from) setFrom(v); }} className="w-40" data-testid="dash-to-input" />
          <Button size="icon" variant="outline" onClick={() => move(1)} data-testid="dash-next-range"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="fade-up d1 mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi label="Receipts issued" value={rows.length} testid="dash-count" />
        <Kpi label="Due at admission" value={sum((c) => c.atAdmission)} testid="dash-at-admission" money />
        <Kpi label="School fee (after disc.)" value={sum((c) => c.schoolFeeAfterDisc)} testid="dash-school-fee" money />
        <Kpi label="Discounts given" value={sum((c) => c.totalDiscount)} testid="dash-discount" money />
        <Kpi label={`Form fees collected · ${formFees.length}`} value={formFees.reduce((t, r) => t + Number(r.form_fee.amount || 0), 0)} testid="dash-form-fees" money />
      </div>

      <div className="fade-up d2 mb-4"><ReferralBoard rows={rows} /></div>

      <div className="fade-up d2 mb-4"><SpotExceptions all={data} from={from} to={to} fileTag={fileTag} /></div>

      {isAdmin && <div className="fade-up d2 mb-4"><LateEntries all={data} /></div>}

      <div className="fade-up d2 mb-4"><Leaderboard rows={rows} /></div>

      <div className="fade-up d2 grid gap-4 lg:grid-cols-3">
        <div className="lg:row-span-2"><Bars title="By Grade" items={byGrade} testid="dash-by-grade" /></div>
        <Bars title="By Category" items={byCat} testid="dash-by-category" />
        <Bars title="By Sub Category" items={bySub} testid="dash-by-subcategory" />
        <div className="rounded-lg border border-plum-900/10 bg-white p-5 lg:col-span-2" data-testid="dash-receipts-list">
          <h2 className="font-display mb-3 text-base font-bold text-plum-950">Receipts in this period</h2>
          {!rows.length && <p className="py-6 text-sm text-slate-500" data-testid="dash-empty">No receipts issued in this period.</p>}
          <div className="divide-y divide-slate-100">
            {rows.map((r) => (
              <button key={r.id} onClick={() => nav(`/receipts/${r.id}`)} data-testid={`dash-row-${r.receipt_no}`}
                className="flex w-full items-center justify-between gap-3 py-2.5 text-left text-sm transition-colors duration-150 hover:bg-slate-50">
                <span><span className="font-num mr-3 text-slate-500">{r.receipt_no}</span><span className="font-medium">{r.inputs.childName}</span>
                  <span className="ml-2 text-xs text-slate-500">{r.inputs.grade} · {catLabel(r.inputs)} · {new Date(r.created_at).toLocaleDateString("en-IN")}</span></span>
                <span className="font-num font-medium">₹ {inr(r.computed.atAdmission)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
